import { TZDateMini } from '@date-fns/tz';
import { isAfter, isBefore, isValid } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { useAriaLabelingProps } from '../../../core/hooks/useAriaLabelingProps.js';
import { usePrevious } from '../../../core/hooks/usePrevious.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import {
  getTimeValue,
  removePrecision,
} from '../../forms-core/time/timeParser.js';
import type {
  DateSelectorProps,
  DateTimePickerProps,
  DateTimePickerType,
} from '../types.js';
import { useDateTimeFormat } from './useDateTimeFormat.js';
import { useSpinButtons } from './useSpinButtons.js';
import type { TimeValue } from '../../../core/types/time.js';

/**
 * Hook to provide the validation and 2way binding between the iso date and the rendered spinbuttons.
 */
export function useDateTimePicker(
  // 'type' may be 'never', but for the hook we always want to accept it.
  props: Omit<DateTimePickerProps, 'type'> & {
    type?: DateTimePickerType;
    valueType?: TimeValue['type'];
    value: string | null;
  },
) {
  const {
    type,
    precision,
    min,
    max,
    disabled = false,
    readOnly = false,
    value: propValue,
    onChange,
    required,
    valueType,
  } = props;

  // The spin buttons are only visible and interactive
  // in 'iso8601' mode and should be disabled for expression input
  const isDisabled = disabled || valueType !== 'iso8601';

  const timezone = getTimezone();

  // use the aria label of the datetime picker to prefix each spinbutton aria label
  const { 'aria-label': ariaLabelPrefix } = useAriaLabelingProps(props);

  const value = propValue === '' ? null : propValue;

  const [isoDateTime, setIsoDateTime] = useState(
    getTimeValue(value ?? undefined, precision)?.absoluteDate ?? null,
  );

  // Tracks the intended isoDateTime value synchronously, before React commits the
  // new state. The TimeframeSelector's setDateTimeRangePickerTimeframe always updates
  // the Zustand store synchronously inside the dateTime effect (before React commits
  // isoDateTime). This triggers a subscriber re-render where spinButtonValue is already
  // the new value but isoDateTime in the closure is still the old stale value. The
  // controlled value effect would then call applyDateTime unnecessarily, resetting the
  // spinbutton cursor to position 0 so that the next digit typed would be rejected as
  // too long (candidate "00020" exceeds the 4-digit max). By updating this ref before
  // calling setIsoDateTime, the controlled value effect always compares against the
  // correct pending value regardless of when Zustand triggers its re-render.
  const pendingIsoDateTimeRef = useRef(isoDateTime);
  const { toParts, emptyParts } = useDateTimeFormat(type, precision);
  const {
    state: { isValid: isValidFormControl },
  } = useFormFieldMessagesContext();
  const { spinButtons, dateTime, updateParts, handlers, parts, toDateTime } =
    useSpinButtons(
      isoDateTime,
      type,
      precision,
      isDisabled,
      readOnly,
      ariaLabelPrefix,
      value,
    );
  const prevDateTime = usePrevious(dateTime);

  const minDateTime = useMemo(() => {
    const candidate =
      min !== undefined ? new TZDateMini(min, timezone) : undefined;
    return isValid(candidate) ? candidate : undefined;
  }, [min, timezone]);
  const maxDateTime = useMemo(() => {
    const candidate =
      max !== undefined ? new TZDateMini(max, timezone) : undefined;
    return isValid(candidate) ? candidate : undefined;
  }, [max, timezone]);

  /**
   * Callback to apply a given iso date to the currently rendered spinbuttons
   * Accepts a pass filter to apply the iso datetime to only a subset of the spinbuttons.
   * Returns true, if the iso date was applied successfully. False otherwise.
   */
  const applyDateTime = useCallback(
    (
      isoDateTime: string | null,
      passFilter?: Set<Intl.DateTimeFormatPart['type']>,
    ) => {
      if (isoDateTime === null) {
        updateParts(emptyParts);
        return true;
      }

      const newDate = new TZDateMini(isoDateTime, timezone);

      // Add timezone offset for type 'date' to have the correct date when there is no time information (2025-01-01Z).
      newDate.setMinutes(
        newDate.getMinutes() +
          (type === 'date' ? newDate.getTimezoneOffset() : 0),
      );

      if (!isValid(newDate)) {
        return false;
      }
      const newDateParts = toParts(newDate);
      passFilter
        ? updateParts(newDateParts.filter((p) => passFilter.has(p.type)))
        : updateParts(newDateParts);

      return true;
    },
    [emptyParts, timezone, toParts, type, updateParts],
  );

  const dateSelectorProps: DateSelectorProps = useMemo(
    () => ({
      value: isoDateTime ?? TZDateMini.tz(timezone).toISOString(),
      onChange: (newIsoString: string) => {
        // Only apply the date part of the iso date returned by the calendar.
        // Don't touch the time values, but set the focus to the hour field in the next render cycle.
        applyDateTime(newIsoString, new Set(['year', 'month', 'day']));
        // Selecting a date in the calendar closes the overlay and restores the focus to the date picker trigger.
        // In order to set the focus to the time spin buttons, we need to defer setting the focus.
        requestAnimationFrame(() =>
          handlers.get('hour')?.element.current?.focus(),
        );
      },
      disabled: disabled || readOnly,
      error: false,
    }),
    [isoDateTime, timezone, disabled, readOnly, applyDateTime, handlers],
  );

  // Show the error state, if the value is outside of given bounds or the date object is invalid.
  const error = useMemo(() => {
    if (!isValidFormControl) {
      return true;
    }

    if (!required && isoDateTime === null) {
      return false;
    }

    return (
      (dateTime === null ||
        !isValid(dateTime) ||
        (minDateTime && isBefore(dateTime, minDateTime)) ||
        (maxDateTime && isAfter(dateTime, maxDateTime))) ??
      false
    );
  }, [
    isValidFormControl,
    required,
    isoDateTime,
    dateTime,
    minDateTime,
    maxDateTime,
  ]);

  // true, if none of the content parts contains any content
  const empty = useMemo(
    () => !parts.some((part) => part.type !== 'literal' && part.value !== ''),
    [parts],
  );

  // If the datetime is valid, convert to an ISO8601 datetime string.
  useEffect(() => {
    // Don't trigger an 'onChange' on initial render or if the value didn't actually change.
    const numericDateTime = +(dateTime || 0);
    const numericPrevDateTime = +(prevDateTime || 0);
    if (
      prevDateTime === undefined ||
      (Number.isNaN(numericDateTime) && Number.isNaN(numericPrevDateTime)) ||
      numericDateTime === numericPrevDateTime
    ) {
      return;
    }

    // propagate null dateTime to isoDateTime
    if (dateTime === null) {
      pendingIsoDateTimeRef.current = null;
      setIsoDateTime(null);
      onChange?.(null);
      return;
    }

    // only allow valid datetime values
    if (!isValid(dateTime)) {
      pendingIsoDateTimeRef.current = null;
      setIsoDateTime(null);
      onChange?.('Invalid Date');
      return;
    }

    const newIsoDateTime = dateTime.toISOString();

    // ignore if nothing has changed
    if (isoDateTime === newIsoDateTime) {
      return;
    }

    // Update the ref synchronously before calling setIsoDateTime so that any
    // Zustand subscriber re-render triggered synchronously by the onChange chain
    // below already sees the correct pending value in the controlled value effect.
    pendingIsoDateTimeRef.current = newIsoDateTime;
    setIsoDateTime(newIsoDateTime);
    onChange?.(getTimeValue(newIsoDateTime ?? null, precision) ?? null);

    // Don't run on an onChange callback update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateTime, prevDateTime]);

  // If the controlled value has changed, apply the new value to the spinbuttons
  // Don't combine with the above effect. This effect needs to be run in the next render cycle.
  useEffect(() => {
    // only apply if value is different (prevents issues while typing and prefixing with 0)
    if (
      removePrecision(
        pendingIsoDateTimeRef.current ?? '',
        type === 'date' ? 'day' : precision,
      ) !== value
    ) {
      applyDateTime(
        getTimeValue(value ?? undefined, precision)?.absoluteDate ?? null,
      );
    }

    // Only run if the controlled prop value is updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return {
    isoDateTime,
    spinButtons,
    handlers,
    dateSelectorProps,
    error,
    empty,
    applyDateTime,
    firstSpinButtonHandler: handlers.get(parts[0]?.type),
    dateTime,
    toDateTime,
    parts,
  };
}
